import axios from "axios"
import API from "@/utils/api"
import { buildPublicCustomerOrderRefundDetailsPath } from "@/services/public-storefront-paths"
import { mapOrderRefundDetails, type OrderRefundDetailsUI } from "@/domain/order/order-refund-details"

type RefundDetailsEnvelope = {
  resultCode?: number
  success?: boolean
  message?: unknown
  data?: unknown
}

function mapRefundDetailsLoadError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401) {
      return new Error("Your session has expired. Please log in again.")
    }
    if (status === 404) {
      return new Error("Refund details are not available for this order.")
    }
    if (status === 403) {
      return new Error("You do not have access to this order.")
    }
  }
  return new Error("Unable to load refund details. Try again.")
}

/**
 * Loads refund details for one of the customer's cancelled orders — only when the customer asks.
 * Uses the tenant-scoped GET /api/public/v1/storefront/orders/{orderId}/refund-details; the server
 * decides ownership, tenant and eligibility. Callers own UX — interceptor toasts are suppressed.
 */
export async function fetchOrderRefundDetails(
  orderId: string,
  signal?: AbortSignal
): Promise<OrderRefundDetailsUI> {
  try {
    const res = await API.get<RefundDetailsEnvelope>(`/${buildPublicCustomerOrderRefundDetailsPath(orderId)}`, {
      signal,
      skipErrorToast: true,
    })
    const envelope = res?.data
    if (envelope?.resultCode !== 200 || !envelope?.success) {
      throw new Error("Unable to load refund details. Try again.")
    }
    return mapOrderRefundDetails(envelope.data)
  } catch (error) {
    throw mapRefundDetailsLoadError(error)
  }
}
